import { BreakpointObserver, BreakpointState, Breakpoints } from '@angular/cdk/layout';
import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { RxState } from '@rx-angular/state';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyFilterChipsModule, GalaxyFilterInterface } from '@vendasta/galaxy/filter/chips';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyDataSource, GalaxyTableModule, Row } from '@vendasta/galaxy/table';
import { GalaxyColumnDef } from '@vendasta/galaxy/table/src/table.interface';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Association } from '../../shared-services/crm-services/crm-object-service/crm-association.service';
import { LIST_OBJECTS_DEFAULT_PAGE_SIZE, LIST_OBJECTS_PAGE_SIZE_OPTIONS, SNACKBAR_DURATION } from '../../constants';
import { ObjectType } from '../../tokens-and-interfaces';
import { TranslationModule } from '../../i18n/translation-module';
import { ListObjectsTableComponent } from '../list-objects/list-objects-table.component';
import { ListObjectsTableService } from '../list-objects/list-objects-table.service';
import { TranslateByObjectTypePipe } from '../../i18n/translate-by-object-type/translate-by-object-type.pipe';
import { TranslateByObjectTypeService } from '../../i18n/translate-by-object-type/translate-by-object-type.service';

export interface ListObjectTypeDialogData {
  baseColumnIds: string[];
  modalTitle: string;
  objectType: ObjectType;
  associations: Association[];
  associationType: string;
  newSelection: string;
  enableMultiplePreSelectedRows?: boolean;
  newSelectionIDs?: string[];
  hideCreateButton: boolean;
  singleSelection?: boolean;
}
export interface ListObjectTypeDialogResult {
  selectedRows: Row[];
  clickedCreate?: boolean;
}

@Component({
  selector: 'crm-select-object-type-modal',
  templateUrl: './select-object-type-modal.component.html',
  styleUrls: ['./select-object-type-modal.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    TranslationModule,
    MatDialogModule,
    AsyncPipe,
    ListObjectsTableComponent,
    GalaxyLoadingSpinnerModule,
    GalaxyEmptyStateModule,
    GalaxyTableModule,
    MatTableModule,
    GalaxyFilterChipsModule,
    TranslateByObjectTypePipe,
  ],
  providers: [ListObjectsTableService, RxState],
  standalone: true,
})
export class SelectObjectTypeModalComponent {
  dataSource: GalaxyDataSource<Row, GalaxyFilterInterface, MatSort> = this.service.dataSource;
  columns$: Observable<GalaxyColumnDef[]> = this.service.columns$;
  initialLoading$: Observable<boolean> = this.service.initialLoading$;
  showObjects$: Observable<boolean> = this.service.showObjects$;
  showEmptyState$: Observable<boolean> = this.service.showEmptyState$;
  objectType: ObjectType;
  selectedRows: Row[] = [];
  showFiltersApplied$: Observable<boolean> = this.dataSource.filtersApplied$;
  isMobile$: Observable<boolean>;

  newSelectedRow: Row[] = [
    {
      id: this.data?.newSelection,
      data: {},
    } as Row,
  ];
  enablePreselectedRows: boolean = this.data?.enableMultiplePreSelectedRows ?? false;
  preSelectedRows?: Row[] = this.data?.newSelectionIDs?.map((id) => {
    return {
      id,
      data: {},
    } as Row;
  });
  newSelection: Row[] = this.enablePreselectedRows ? this.preSelectedRows ?? [] : this.newSelectedRow;

  shouldOpenSelectModal = false;

  pageSizeOptions: number[] = LIST_OBJECTS_PAGE_SIZE_OPTIONS;
  pageSize: number = LIST_OBJECTS_DEFAULT_PAGE_SIZE;

  hasAssociations: boolean = this.data.associations.length > 0 ? true : false;
  preSelectedLockedRow: Row[] = this.data.associations.map((association) => {
    return {
      id: association?.associatedObject?.crmObjectId,
      data: {},
    } as Row;
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ListObjectTypeDialogData,
    private readonly service: ListObjectsTableService,
    private dialogRef: MatDialogRef<SelectObjectTypeModalComponent>,
    private readonly snackService: SnackbarService,
    private readonly translateService: TranslateService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly translateByObjectTypeService: TranslateByObjectTypeService,
  ) {
    this.service.setObjectType(this.data.objectType);
    this.service.setShowActions(false);
    this.service.setBaseColumns(this.data.baseColumnIds);
    this.objectType = this.data.objectType;
    this.isMobile$ = this.breakpointObserver.observe(Breakpoints.XSmall).pipe(
      startWith({
        matches: false,
        breakpoints: {
          [Breakpoints.XSmall]: false,
        },
      } as BreakpointState),
      map((breakpoint: BreakpointState) => breakpoint.matches),
    );
  }

  updateFilters(filters: GalaxyFilterInterface[]): void {
    this.service.filters = filters;
    this.dataSource.setFilters(filters);
  }

  selectionChanged(rows: Row[]): void {
    if (this.preSelectedLockedRow.length > 0) {
      const filteredData = rows.filter((data) => !this.preSelectedLockedRow.some((item) => item.id === data.id));
      this.selectedRows = filteredData;
      return;
    }
    this.selectedRows = rows;
  }

  cancel(): void {
    this.dialogRef.close();
  }

  selectContacts(): void {
    if (this.selectedRows.length > 0) {
      this.dialogRef.close({ selectedRows: this.selectedRows });
    } else {
      const translatedString = this.translateByObjectTypeService.getTranslationByObjectType(
        this.objectType,
        'SELECT_MODAL.NO_SELECTED',
      );
      this.snackService.openWithOptions(this.translateService.instant(translatedString), {
        duration: SNACKBAR_DURATION,
      });
    }
  }

  saveCompanies(): void {
    if (this.selectedRows.length > 0) {
      this.dialogRef.close({ selectedRows: this.selectedRows });
    } else {
      const translatedString = this.translateByObjectTypeService.getTranslationByObjectType(
        this.objectType,
        'SELECT_MODAL.NO_SELECTED',
      );
      this.snackService.openWithOptions(this.translateService.instant(translatedString), {
        duration: SNACKBAR_DURATION,
      });
    }
  }

  createContactModal(): void {
    this.shouldOpenSelectModal = true;

    this.dialogRef.close({ selectedRows: this.selectedRows, clickedCreate: true } as ListObjectTypeDialogResult);
  }

  save(): void {
    if (this.selectedRows.length > 0) {
      this.dialogRef.close({ selectedRows: this.selectedRows });
    } else {
      const translatedString = this.translateByObjectTypeService.getTranslationByObjectType(
        this.objectType,
        'SELECT_MODAL.NO_SELECTED',
      );
      this.snackService.openWithOptions(this.translateService.instant(translatedString), {
        duration: SNACKBAR_DURATION,
      });
    }
  }
}
